import { AgenciaOrcamentoPolicy } from './agenciaOrcamentoPolicy';
import { ehSomenteVisualizacao } from './somenteVisualizacaoPolicy';
import { comNfPropria } from './impostoNfContaDiretaPolicy';

const toNumber = (value) => {
    if (value === null || value === undefined || value === '') {
        return 0;
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }

    const stringValue = String(value).trim();
    if (!stringValue) {
        return 0;
    }

    if (stringValue.includes(',')) {
        return parseFloat(stringValue.replace(/\./g, '').replace(',', '.')) || 0;
    }

    return parseFloat(stringValue) || 0;
};

const toComparableId = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    if (typeof value === 'object') {
        const objectId = value?.id ?? value?.value ?? null;
        if (objectId === null || objectId === undefined || objectId === '' || typeof objectId === 'object') {
            return null;
        }
        return String(objectId);
    }

    return String(value);
};

const normalize = (text) =>
    (text || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase();

const toBooleanFlag = (value) => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value === 1;
    }

    return ['1', 'TRUE', 'SIM', 'YES', 'ON'].includes(normalize(value));
};

const isAgencyGroup = (item = {}) => {
    return AgenciaOrcamentoPolicy.isGrupoAgencia(item);
};

const parseOrcamentoConfig = (config) => {
    if (!config) {
        return null;
    }

    if (typeof config === 'string') {
        try {
            return JSON.parse(config);
        } catch (error) {
            return null;
        }
    }

    if (typeof config === 'object') {
        return config;
    }

    return null;
};

const isFeeDebugEnabled = () => {
    if (typeof window === 'undefined') {
        return false;
    }
    return window.__debugFee === true;
};

const isAgencyFeeItem = (produto = {}) => {
    const superior = toComparableId(produto.superior);
    const superiorCompativel = !superior || superior === '939529851763065082';
    return AgenciaOrcamentoPolicy.isFeeAgenciaGlobal(produto) && superiorCompativel;
};

const isSanofiAgencyTierFeeItem = (produto = {}) => {
    const codigoFornecedor = normalize(produto?.codigoFornecedor);
    return codigoFornecedor === 'ACCESS1001' || codigoFornecedor === 'ACCESS1002';
};

const isVoucherManualBaseItem = (produto = {}) => {
    const config = parseOrcamentoConfig(produto?.orcamentoConfig ?? produto?.orcamento_config);
    const feeConfig = config?.fee ?? {};
    const manualBaseConfig =
        feeConfig.manualBasePercent === true ||
        feeConfig.manualBasePercent === 'true' ||
        feeConfig.category === 'voucher_manual_base' ||
        feeConfig.itemKey === 'voucher_manual_base';

    if (manualBaseConfig) {
        return true;
    }

    const nome = normalize(produto.nome);
    return nome === 'UBER' ||
        nome === 'IFOOD';
};

const normalizeMoment = (moment = 'pre_evento') => {
    if (moment === 'negociado') return 'negociado';
    if (moment === 'fechamento') return 'fechamento';
    return 'pre_evento';
};

const normalizeClientFeeRules = (rules = []) => {
    if (!Array.isArray(rules)) {
        return [];
    }

    return rules
        .map((regra, index) => ({
            percentual: toNumber(regra?.percentual ?? regra?.valor ?? 0),
            momento: (regra?.momento ?? 'todos').toString().toLowerCase(),
            faixaMinTotal: regra?.faixaMinTotal === null || regra?.faixaMinTotal === undefined || regra?.faixaMinTotal === ''
                ? 0
                : toNumber(regra.faixaMinTotal),
            faixaMaxTotal: regra?.faixaMaxTotal === null || regra?.faixaMaxTotal === undefined || regra?.faixaMaxTotal === ''
                ? null
                : toNumber(regra.faixaMaxTotal),
            ordem: Number(regra?.ordem ?? (index + 1)),
        }))
        .filter((regra) => regra.percentual > 0)
        .sort((a, b) => {
            if (a.ordem !== b.ordem) {
                return a.ordem - b.ordem;
            }
            return (a.faixaMinTotal ?? 0) - (b.faixaMinTotal ?? 0);
        });
};

const resolveClientFeePercent = (rules = [], moment = 'pre_evento', totalBase = 0) => {
    const momentoAtual = normalizeMoment(moment);
    const elegiveisMomento = rules.filter((regra) => regra.momento === 'todos' || regra.momento === momentoAtual);

    for (const regra of elegiveisMomento) {
        const min = regra.faixaMinTotal ?? 0;
        const max = regra.faixaMaxTotal;
        const minOk = totalBase >= min;
        const maxOk = max === null || max === undefined || totalBase <= max;
        if (minOk && maxOk) {
            return regra.percentual;
        }
    }

    return 0;
};

const feeBaseType = (produto = {}) => produto.feeBaseTipo ?? 'compoe_fee_global';

const normalizePriceType = (tipoPreco) => (tipoPreco ?? 'fixo').toString().toLowerCase();

const parseFeeBaseIds = (value) => {
    if (value === null || value === undefined || value === '') {
        return [];
    }

    if (Array.isArray(value)) {
        return value
            .map((entry) => toComparableId(entry))
            .filter(Boolean);
    }

    return String(value)
        .split(',')
        .map((entry) => toComparableId(entry.trim()))
        .filter(Boolean);
};

const normalizePricingRanges = (produto = {}, tabelaVersao = null) => {
    const source = produto?.precificacaoFaixas ?? produto?.precificacao_faixas ?? [];
    if (!Array.isArray(source)) {
        return [];
    }

    const versaoAlvo = (tabelaVersao === null || tabelaVersao === undefined || tabelaVersao === '') ? null : String(tabelaVersao);

    return source
        .filter((faixa) => {
            // Épico 13 — período: faixa sem versão aplica sempre; com versão, só a do orçamento
            const v = (faixa?.versao === undefined || faixa?.versao === null || faixa?.versao === '') ? null : String(faixa.versao);
            return v === null || v === versaoAlvo;
        })
        .map((faixa, index) => {
            const min = faixa?.faixaMin;
            const max = faixa?.faixaMax;
            return {
                ordem: Number(faixa?.ordem ?? (index + 1)),
                faixaMin: min === null || min === undefined || min === '' ? null : toNumber(min),
                faixaMax: max === null || max === undefined || max === '' ? null : toNumber(max),
                tipoPreco: normalizePriceType(faixa?.tipoPreco),
                valor: toNumber(faixa?.valor),
            };
        })
        .sort((a, b) => {
            if (a.ordem !== b.ordem) {
                return a.ordem - b.ordem;
            }
            return (a.faixaMin ?? 0) - (b.faixaMin ?? 0);
        });
};

const pickRangeByQuantity = (ranges = [], quantity = 0) => {
    if (!Array.isArray(ranges) || ranges.length === 0) {
        return null;
    }

    const qty = toNumber(quantity);
    for (const range of ranges) {
        const min = range.faixaMin ?? 0;
        const max = range.faixaMax;
        const minOk = qty >= min;
        const maxOk = max === null || max === undefined || qty <= max;
        if (minOk && maxOk) {
            return range;
        }
    }

    const first = ranges[0];
    const last = ranges[ranges.length - 1];
    if (qty < (first.faixaMin ?? 0)) {
        return first;
    }
    return last;
};

const isAgencyFeeRuleItem = (produto = {}) => {
    const baseTipo = feeBaseType(produto);
    const tipoPreco = normalizePriceType(produto.tipoPreco);
    if (isSanofiAgencyTierFeeItem(produto) && tipoPreco === 'percentual') {
        return true;
    }
    if (isVoucherManualBaseItem(produto)) {
        return false;
    }
    return tipoPreco === 'percentual' &&
        !isAgencyFeeItem(produto) &&
        (toBooleanFlag(produto.somarTodosFee) || baseTipo !== 'compoe_fee_global');
};

const isFeeCalculatorItem = (produto = {}) => {
    return isAgencyFeeItem(produto) || isAgencyFeeRuleItem(produto);
};

const isPaidByAgency = (subitem = {}) => {
    const fornecedorPagamentoId = (subitem.fornecedorpagamento?.id ?? subitem.fornecedorpagamento ?? '').toString().toLowerCase();
    // Se estiver explicitamente como "cliente", não compõe fee da agência.
    // Vazio/null mantém compatibilidade com orçamentos antigos e considera como agência.
    return fornecedorPagamentoId !== 'cliente';
};

const compoeFee = (subitem = {}) => toBooleanFlag(subitem.compoeFee);

// Moeda do fornecedor (27/09): a linha com câmbio próprio chega anotada com o fator
// (moedaFornecedorPolicy.anotarFatoresMoeda) que leva o valor dela para a moeda do orçamento.
// Sem anotação (legado) o fator é 1: nenhuma conta muda.
const fatorMoedaMomento = (subitem = {}, moment) => {
    const f = subitem?.fatorMoedaOrcamento?.[moment];
    return typeof f === 'number' && Number.isFinite(f) ? f : null;
};

const totalByMoment = (subitem = {}, moment) => {
    const fator = fatorMoedaMomento(subitem, moment);
    const total = moment === 'pre_evento'
        ? toNumber(subitem.totalPreEvento)
        : moment === 'negociado' ? toNumber(subitem.totalNegociado) : toNumber(subitem.totalFechamento);
    return fator === null ? total : total * fator;
};

const quantityByMoment = (subitem = {}, moment) => {
    if (moment === 'pre_evento') {
        return toNumber(subitem.quantidadePreEvento);
    }
    if (moment === 'negociado') {
        return toNumber(subitem.quantidadeNegociado);
    }
    return toNumber(subitem.quantidadeFechamento);
};

const dailyByMoment = (subitem = {}, moment) => {
    if (moment === 'pre_evento') {
        return toNumber(subitem.diariaPreEvento);
    }
    if (moment === 'negociado') {
        return toNumber(subitem.diariaNegociado);
    }
    return toNumber(subitem.diariaFechamento);
};

const unitByMoment = (subitem = {}, moment) => {
    if (moment === 'pre_evento') {
        return toNumber(subitem.valorUnitarioPreEvento);
    }
    if (moment === 'negociado') {
        return toNumber(subitem.valorUnitarioNegociado);
    }
    return toNumber(subitem.valorUnitarioFechamento);
};

// Base do Fee Agência (decisão 2026-09-17): o fee incide sobre o valor limpo
// do item — unit × qtd × diárias — nunca sobre o total com % impostos ou gross-up
// de imposto NF embutidos.
// Regra B (Rafael 28/09): no nacional Sanofi-família/Chiesi a linha chega anotada com
// feeBaseComServico (feeBaseInternacionalPolicy) e a base soma o % Serviço da etapa:
// unit × qtd × diárias × (1 + % Serviço). Espelho de OrcamentoFeeRuleDisplay::rawBaseByMoment.
// Internacional (decisão 2026-09-25, planilha como referência): a linha chega anotada
// com feeBaseInternacional (feeBaseInternacionalPolicy) = total + encargos da remessa.
// R$ exato (contrato 02/10, regra 4): orçamento estrangeiro com linha de moeda própria chega com
// baseFeeBrlExato (feeBaseInternacionalPolicy.anotarBaseFeeBrlExato) = base da linha em R$ já
// arredondada; aqui vai para a moeda do orçamento (÷ câmbio), sem fator, sem novo arredondamento.
const baseBrlExatoMomento = (subitem = {}, moment) => {
    const anotada = subitem?.baseFeeBrlExato;
    if (!anotada || typeof anotada[moment] !== 'number' || !Number.isFinite(anotada[moment])) return null;
    const cambio = toNumber(anotada.cambio);
    return cambio > 0 ? anotada[moment] / cambio : null;
};

const rawBaseByMoment = (subitem = {}, moment) => {
    // Item só para visualização (D-0113): a opção não entra em base de FEE nenhuma.
    if (ehSomenteVisualizacao(subitem)) return 0;
    const exata = baseBrlExatoMomento(subitem, moment);
    if (exata !== null) return exata;
    const fator = fatorMoedaMomento(subitem, moment);
    const baseInternacional = subitem?.feeBaseInternacional;
    if (baseInternacional && baseInternacional[moment] !== undefined && baseInternacional[moment] !== null) {
        const base = toNumber(baseInternacional[moment]);
        return fator === null ? base : base * fator;
    }
    let base = quantityByMoment(subitem, moment) * dailyByMoment(subitem, moment) * unitByMoment(subitem, moment);
    if (subitem?.feeBaseComServico) base *= 1 + (serviceByMoment(subitem, moment) / 100);
    // AbbVie nacional (06/10): "Total c/ imposto" da planilha — % Serviço + % Impostos do item (NF fora).
    else if (subitem?.feeBaseComImpostoItem) base *= 1 + (serviceByMoment(subitem, moment) / 100) + (toNumber(subitem.impostoPorcentagemItem) / 100);
    return fator === null ? base : base * fator;
};

const serviceByMoment = (subitem = {}, moment) => {
    if (moment === 'pre_evento') {
        return toNumber(subitem.servicoPreEvento);
    }
    if (moment === 'negociado') {
        return toNumber(subitem.servicoNegociado);
    }
    return toNumber(subitem.servicoFechamento);
};

// Total da linha de FEE de regra SEM arredondar (R$ exato: o R$ do FEE é arredondado uma vez só,
// depois de × câmbio — valorBrlPolicy.valorBrlLinha). null = não é FEE de regra percentual.
const calculateAgencyFeeLineTotalRaw = (subitem = {}, moment = 'pre_evento') => {
    const produto = subitem.produto ?? {};
    if (!isAgencyFeeRuleItem(produto) || normalizePriceType(produto.tipoPreco) !== 'percentual') {
        return null;
    }

    const contribuicaoFee = rawBaseByMoment(subitem, moment) * (serviceByMoment(subitem, moment) / 100);
    const impostoNf = toNumber(subitem.imposto);

    // D-0112: % Imposto NF na conta da linha (direta = × (1 + a)).
    return comNfPropria(contribuicaoFee, impostoNf, subitem);
};

const calculateAgencyFeeLineTotal = (subitem = {}, moment = 'pre_evento') => {
    const total = calculateAgencyFeeLineTotalRaw(subitem, moment);
    if (total === null) return null;
    return Math.round((total + Number.EPSILON) * 100) / 100;
};

const percentualImpostoNfLinhaFee = (subitem = {}) => {
    const impostoNf = toNumber(subitem.imposto);
    return impostoNf > 0 && impostoNf < 100 ? impostoNf : 0;
};

// Total da linha de FEE a partir da contribuição (base × %), igual ao Pré/Negociado:
// FEE de regra (2043…) = contribuição ÷ (1 − % Imposto NF) (calculateAgencyFeeLineTotal);
// FEE AGÊNCIA 130 = contribuição × (1 + % Impostos) ÷ (1 − % Imposto NF) (total de linha comum).
// Sem % Imposto NF na linha (Chiesi com a NF do FEE na 1945 ou por fora do grupo) não muda nada.
const totalLinhaFee = (subitem = {}, contribuicao = 0, especial = false) => {
    let total = toNumber(contribuicao);
    if (!especial) {
        const impostos = toNumber(subitem.impostoPorcentagemItem);
        if (impostos > 0) total *= (1 + (impostos / 100));
    }
    return comNfPropria(total, percentualImpostoNfLinhaFee(subitem), subitem);
};

const itemFeeContribution = (subitem = {}, moment) => {
    if (ehSomenteVisualizacao(subitem)) return 0;
    const produto = subitem.produto ?? {};
    const tipoPreco = normalizePriceType(produto.tipoPreco);
    const preco = toNumber(produto.preco);
    const quantidade = quantityByMoment(subitem, moment);
    const diaria = dailyByMoment(subitem, moment);
    const valorUnitario = moment === 'pre_evento'
        ? toNumber(subitem.valorUnitarioPreEvento)
        : moment === 'negociado'
            ? toNumber(subitem.valorUnitarioNegociado)
            : toNumber(subitem.valorUnitarioFechamento);

    const baseSemServico = quantidade * diaria * valorUnitario;

    if (preco <= 0) {
        return 0;
    }

    if (tipoPreco === 'percentual') {
        if (isVoucherManualBaseItem(produto)) {
            return baseSemServico * (preco / 100);
        }
        return totalByMoment(subitem, moment) * (preco / 100);
    }

    const servico = serviceByMoment(subitem, moment);
    const base = preco * quantidade * diaria;

    return base * (1 + (servico / 100));
};

const emptyMomentValues = () => ({
    pre_evento: 0,
    negociado: 0,
    fechamento: 0
});

const applyMomentFilter = (values = emptyMomentValues(), feeMomentoCalculo = 'todos') => {
    const filtered = { ...values };
    if (feeMomentoCalculo === 'pre_evento') {
        filtered.negociado = 0;
        filtered.fechamento = 0;
    } else if (feeMomentoCalculo === 'negociado') {
        filtered.pre_evento = 0;
        filtered.fechamento = 0;
    } else if (feeMomentoCalculo === 'fechamento') {
        filtered.pre_evento = 0;
        filtered.negociado = 0;
    }
    return filtered;
};

const sameSubitem = (a = {}, b = {}) => {
    return !!(a?.chaveunica && b?.chaveunica && a.chaveunica === b.chaveunica);
};

const matchesRuleBase = (subitem = {}, produtoRegra = {}) => {
    const baseTipo = feeBaseType(produtoRegra);
    const baseIds = parseFeeBaseIds(produtoRegra.feeBaseId);
    const superiorAtual = toComparableId(subitem?.produto?.superior);
    const produtoAtual = toComparableId(subitem?.produto?.id);
    const atributoAtual = toComparableId(subitem?.produto?.atributo);

    if (baseTipo === 'superior_especifico') {
        // feeBaseId aceita id de grupo (superior) e id de produto especifico
        // (ex.: Interpretes/Tradutores, isolado dentro do grupo SERVICOS).
        // Id com prefixo "-" EXCLUI o produto da base: o grupo continua somando
        // e so os itens com fee ja embutido (planilha Abbvie, "nao soma") saem.
        if (produtoAtual && baseIds.includes(`-${produtoAtual}`)) {
            return false;
        }
        return baseIds.length > 0 && (baseIds.includes(superiorAtual) || baseIds.includes(produtoAtual));
    }
    if (baseTipo === 'atributo_especifico') {
        return baseIds.length > 0 && baseIds.includes(atributoAtual);
    }
    return true;
};

const shouldUseAsRuleBase = (subitem = {}, feeRuleSubitem = {}) => {
    if (sameSubitem(subitem, feeRuleSubitem) || ehSomenteVisualizacao(subitem)) {
        return false;
    }

    if (!subitem?.produto) {
        return false;
    }

    if (isFeeCalculatorItem(subitem.produto)) {
        return false;
    }

    if (!compoeFee(subitem)) {
        return false;
    }

    return true;
};

const calculateRuleBaseTotals = (orcamentoitem = [], feeRuleSubitem = {}) => {
    const totals = emptyMomentValues();
    const produtoRegra = feeRuleSubitem.produto ?? {};

    if (!Array.isArray(orcamentoitem)) {
        return totals;
    }

    orcamentoitem.forEach((item) => {
        (item?.subitem || []).forEach((subitem) => {
            if (!shouldUseAsRuleBase(subitem, feeRuleSubitem)) {
                return;
            }
            if (!matchesRuleBase(subitem, produtoRegra)) {
                return;
            }

            totals.pre_evento += rawBaseByMoment(subitem, 'pre_evento');
            totals.negociado += rawBaseByMoment(subitem, 'negociado');
            totals.fechamento += rawBaseByMoment(subitem, 'fechamento');
        });
    });

    return applyMomentFilter(totals, produtoRegra.feeMomentoCalculo ?? 'todos');
};

const calculateRuleContributionValues = (orcamentoitem = [], feeRuleSubitem = {}, tabelaVersao = null, clientRules = []) => {
    const produto = feeRuleSubitem.produto ?? {};
    const defaultTipoPreco = normalizePriceType(produto.tipoPreco);
    const defaultPreco = toNumber(produto.preco);
    const ranges = normalizePricingRanges(produto, tabelaVersao);
    // Faixa própria do produto (por pax) tem precedência; as regras do cliente
    // só entram quando o produto não define faixas para a versão em vigor.
    const regrasCliente = ranges.length === 0 ? normalizeClientFeeRules(clientRules) : [];
    const totals = emptyMomentValues();
    const bases = emptyMomentValues();

    if (!Array.isArray(orcamentoitem)) {
        return {
            ...totals,
            percentuais: emptyMomentValues(),
        };
    }

    const subitensBase = [];
    orcamentoitem.forEach((item) => {
        (item?.subitem || []).forEach((subitem) => {
            if (!shouldUseAsRuleBase(subitem, feeRuleSubitem)) {
                return;
            }
            if (!matchesRuleBase(subitem, produto)) {
                return;
            }
            subitensBase.push(subitem);
            bases.pre_evento += rawBaseByMoment(subitem, 'pre_evento');
            bases.negociado += rawBaseByMoment(subitem, 'negociado');
            bases.fechamento += rawBaseByMoment(subitem, 'fechamento');
        });
    });

    // Épico 12 no fee de catálogo: a faixa de VALOR da regra do cliente é
    // resolvida sobre a base agregada do momento, nunca item a item — senão um
    // orçamento acima do teto da faixa voltaria ao percentual da faixa menor.
    const percentualCliente = {
        pre_evento: regrasCliente.length > 0 ? resolveClientFeePercent(regrasCliente, 'pre_evento', bases.pre_evento) : 0,
        negociado: regrasCliente.length > 0 ? resolveClientFeePercent(regrasCliente, 'negociado', bases.negociado) : 0,
        fechamento: regrasCliente.length > 0 ? resolveClientFeePercent(regrasCliente, 'fechamento', bases.fechamento) : 0,
    };

    const calcularMomento = (subitem, moment) => {
        const base = rawBaseByMoment(subitem, moment);
        const quantidade = quantityByMoment(subitem, moment);

        let tipoPreco = defaultTipoPreco;
        let valor = defaultPreco;
        if (ranges.length > 0) {
            const faixa = pickRangeByQuantity(ranges, quantidade);
            if (faixa) {
                tipoPreco = normalizePriceType(faixa.tipoPreco);
                valor = toNumber(faixa.valor);
            }
        } else if (percentualCliente[moment] > 0) {
            tipoPreco = 'percentual';
            valor = percentualCliente[moment];
        }

        if (valor <= 0) {
            return { base, contribution: 0 };
        }

        if (tipoPreco === 'percentual') {
            return { base, contribution: base * (valor / 100) };
        }

        return { base, contribution: valor };
    };

    subitensBase.forEach((subitem) => {
        totals.pre_evento += calcularMomento(subitem, 'pre_evento').contribution;
        totals.negociado += calcularMomento(subitem, 'negociado').contribution;
        totals.fechamento += calcularMomento(subitem, 'fechamento').contribution;
    });

    const filteredTotals = applyMomentFilter(totals, produto.feeMomentoCalculo ?? 'todos');
    const filteredBases = applyMomentFilter(bases, produto.feeMomentoCalculo ?? 'todos');

    // Base zerada (orçamento começando / etapa vazia): o % vem da regra da versão escolhida,
    // como a API (pickVersionRate). Antes ficava 0 e a tela caía no produto.preco — o 6,19 de
    // 2025 com a Tabela do FEE em 2026 (Sanofi, 08/10). Sem regra do cliente segue 0 (legado).
    const percentualSemBase = (moment) => (ranges.length === 0 ? percentualCliente[moment] : 0);
    return {
        ...filteredTotals,
        percentuais: {
            pre_evento: filteredBases.pre_evento > 0 ? ((filteredTotals.pre_evento / filteredBases.pre_evento) * 100) : percentualSemBase('pre_evento'),
            negociado: filteredBases.negociado > 0 ? ((filteredTotals.negociado / filteredBases.negociado) * 100) : percentualSemBase('negociado'),
            fechamento: filteredBases.fechamento > 0 ? ((filteredTotals.fechamento / filteredBases.fechamento) * 100) : percentualSemBase('fechamento'),
        },
    };
};

const shouldIncludeForAgencyFee = (subitem = {}, feeSubitem = {}, itemPrincipal = {}) => {
    if (sameSubitem(subitem, feeSubitem) || ehSomenteVisualizacao(subitem)) {
        return false;
    }

    if (!subitem?.produto) {
        return false;
    }

    if (isAgencyFeeRuleItem(subitem.produto)) {
        return compoeFee(subitem);
    }

    if (isVoucherManualBaseItem(subitem.produto)) {
        return compoeFee(subitem);
    }

    if (isFeeCalculatorItem(subitem.produto)) {
        return false;
    }

    if (!compoeFee(subitem)) {
        return false;
    }

    return true;
};

const shouldIncludeForRangeBase = (subitem = {}, feeSubitem = {}, itemPrincipal = {}) => {
    if (sameSubitem(subitem, feeSubitem)) {
        return false;
    }

    if (!subitem?.produto) {
        return false;
    }

    // Categorias distintas, mantendo a exclusão histórica de serviços internos
    // da base de faixa; a correção de estrutura não muda a precificação.
    if (isAgencyGroup(itemPrincipal) || AgenciaOrcamentoPolicy.isGrupoServicosAgencia(itemPrincipal)) {
        return false;
    }

    // Evita recursividade: itens que calculam fee não entram na base de faixa.
    if (isFeeCalculatorItem(subitem.produto)) {
        return false;
    }

    return true;
};

const calculateRangeBaseTotals = (orcamentoitem = [], feeSubitem = {}) => {
    const totals = emptyMomentValues();

    if (!Array.isArray(orcamentoitem)) {
        return totals;
    }

    orcamentoitem.forEach((item) => {
        (item?.subitem || []).forEach((subitem) => {
            if (!shouldIncludeForRangeBase(subitem, feeSubitem, item)) {
                return;
            }

            totals.pre_evento += rawBaseByMoment(subitem, 'pre_evento');
            totals.negociado += rawBaseByMoment(subitem, 'negociado');
            totals.fechamento += rawBaseByMoment(subitem, 'fechamento');
        });
    });

    return totals;
};

// Abbvie (Épico 13 + decisão 2026-09-17): a linha única "Fee Agência" compõe a
// soma dos fees por categoria do catálogo (2130-2136). A FAIXA é resolvida UMA
// vez pelo nº de participantes do EVENTO (não pela quantidade dos itens) na
// versão da "Tabela de valores" do orçamento; o percentual aplica sobre a base
// limpa (unit × qtd × diárias) dos itens Compõe FEE dos grupos da categoria.
// Mini Meeting da AbbVie (regra do Rafael 28/09) — espelho da API (OrcamentoFeeMiniMeeting):
// evento tipo 'minimeet' com a regra no catálogo (GET /produto/feeMiniMeetingCliente →
// { limite, fixo, percentual }) NÃO usa as categorias. Por etapa, cada uma com a sua base:
// base limpa das linhas Compõe FEE = Sim fora dos grupos AGÊNCIA (FEE), SERVIÇOS AGÊNCIA (3027)
// e AÉREO; base 0 → FEE 0; base ≤ limite → fixo; base > limite → o MAIOR entre o fixo e
// percentual × base (Rafael 28/09, Q1 = B: o FEE nunca cai quando a base cresce).
const MINI_MEETING = 'mini_meeting';
const nomeGrupoCatalogo = (produto = {}) => normalize(produto?.nome ?? '').trim().replace(/^\d+\s*-\s*/, '').replace(/\s+/g, ' ').trim();
const eventoMiniMeeting = (tipoEvento) => typeof tipoEvento === 'string' && tipoEvento.trim().toLowerCase() === 'minimeet';
const miniMeetingParaEvento = (regra, tipoEvento) => (regra && typeof regra === 'object' && eventoMiniMeeting(tipoEvento) ? regra : null);
const grupoForaBaseMiniMeeting = (item = {}) => {
    if (isAgencyGroup(item) || AgenciaOrcamentoPolicy.isGrupoServicosAgencia(item)) return true;
    const nome = nomeGrupoCatalogo(item?.produto ?? {});
    return nome === 'AGENCIA' || nome === 'SERVICOS AGENCIA' || nome === 'AEREO';
};
const calculateMiniMeetingFee = (orcamentoitem = [], regra = {}) => {
    const limite = toNumber(regra?.limite);
    const fixo = toNumber(regra?.fixo);
    const percentual = toNumber(regra?.percentual);
    const bases = emptyMomentValues();
    (Array.isArray(orcamentoitem) ? orcamentoitem : []).forEach((item) => {
        if (grupoForaBaseMiniMeeting(item)) return;
        (item?.subitem || []).forEach((subitem) => {
            if (!subitem?.produto || isFeeCalculatorItem(subitem.produto) || !compoeFee(subitem)) return;
            bases.pre_evento += rawBaseByMoment(subitem, 'pre_evento');
            bases.negociado += rawBaseByMoment(subitem, 'negociado');
            bases.fechamento += rawBaseByMoment(subitem, 'fechamento');
        });
    });
    const resultado = { ...emptyMomentValues(), aplicavel: true, regra: MINI_MEETING, categorias: [], mini_meeting: { limite, fixo, percentual, momentos: {} } };
    const categoria = { codigo: MINI_MEETING, percentual: null };
    Object.keys(bases).forEach((momento) => {
        const base = Math.round(bases[momento] * 1e6) / 1e6;
        let modo = 'sem_base';
        let valor = 0;
        if (base > 0 && base <= limite) { modo = 'fixo'; valor = fixo; }
        else if (base > limite) {
            const valorPercentual = base * percentual / 100;
            modo = valorPercentual > fixo ? 'percentual' : 'minimo';
            valor = Math.max(valorPercentual, fixo);
        }
        resultado[momento] = valor;
        resultado.mini_meeting.momentos[momento] = { base, modo, valor };
        categoria[`base_${momento}`] = base;
    });
    resultado.categorias.push(categoria);
    return resultado;
};
const formatarNumeroBr = (valor, casas) => Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: 2 });
// Texto da regra aplicada numa etapa (igual ao OrcamentoFeeMiniMeeting::rotulo da API).
const rotuloMiniMeeting = (resultado = {}, momento = 'pre_evento') => {
    const mm = resultado?.mini_meeting;
    const etapa = mm?.momentos?.[momento];
    if (!etapa) return null;
    const limite = formatarNumeroBr(mm.limite, 2);
    if (etapa.modo === 'fixo') return `Mini Meeting: fixo até R$ ${limite}`;
    if (etapa.modo === 'minimo') return `Mini Meeting: fixo (mínimo) R$ ${formatarNumeroBr(mm.fixo, 2)}`;
    if (etapa.modo === 'percentual') return `Mini Meeting: ${formatarNumeroBr(mm.percentual, 0)}% acima de R$ ${limite}`;
    return 'Mini Meeting: sem base';
};

const calculateCatalogCategoryFees = (orcamentoitem = [], faixasCliente = [], tabelaVersao = null, paxEvento = 0, miniMeeting = null) => {
    // Mini Meeting (28/09): regra já filtrada pelo tipo do evento (miniMeetingParaEvento) troca as categorias.
    if (miniMeeting) return calculateMiniMeetingFee(orcamentoitem, miniMeeting);
    const totais = emptyMomentValues();
    const porProduto = new Map();
    (Array.isArray(faixasCliente) ? faixasCliente : []).forEach((faixa) => {
        if ((faixa?.feeBaseTipo ?? '') !== 'superior_especifico') return;
        if (normalizePriceType(faixa?.tipoPreco) !== 'percentual') return;
        const chave = toComparableId(faixa?.produtoId);
        if (!chave) return;
        if (!porProduto.has(chave)) {
            porProduto.set(chave, {
                id: chave,
                codigo: faixa?.codigo ?? '',
                tipoPreco: 'percentual',
                preco: 0,
                feeBaseTipo: faixa.feeBaseTipo,
                feeBaseId: faixa?.feeBaseId ?? null,
                feeMomentoCalculo: faixa?.feeMomentoCalculo ?? 'todos',
                precificacaoFaixas: [],
            });
        }
        porProduto.get(chave).precificacaoFaixas.push({
            ordem: faixa?.ordem,
            faixaMin: faixa?.faixaMin,
            faixaMax: faixa?.faixaMax,
            tipoPreco: faixa?.tipoPrecoFaixa ?? faixa?.tipoPreco,
            valor: faixa?.valor,
            versao: faixa?.versao,
        });
    });

    const resultado = { ...totais, aplicavel: porProduto.size > 0, categorias: [] };
    if (!resultado.aplicavel || !Array.isArray(orcamentoitem)) {
        return resultado;
    }

    const pax = toNumber(paxEvento);
    porProduto.forEach((produtoVirtual) => {
        const ranges = normalizePricingRanges(produtoVirtual, tabelaVersao);
        if (ranges.length === 0) return;
        const faixa = pickRangeByQuantity(ranges, pax);
        if (!faixa || normalizePriceType(faixa.tipoPreco) !== 'percentual' || toNumber(faixa.valor) <= 0) return;
        const percentual = toNumber(faixa.valor);
        const feeRuleSubitem = { produto: produtoVirtual };
        const bases = emptyMomentValues();
        orcamentoitem.forEach((item) => {
            (item?.subitem || []).forEach((subitem) => {
                if (!shouldUseAsRuleBase(subitem, feeRuleSubitem)) return;
                if (!matchesRuleBase(subitem, produtoVirtual)) return;
                bases.pre_evento += rawBaseByMoment(subitem, 'pre_evento');
                bases.negociado += rawBaseByMoment(subitem, 'negociado');
                bases.fechamento += rawBaseByMoment(subitem, 'fechamento');
            });
        });
        const filtradas = applyMomentFilter(bases, produtoVirtual.feeMomentoCalculo ?? 'todos');
        resultado.pre_evento += filtradas.pre_evento * (percentual / 100);
        resultado.negociado += filtradas.negociado * (percentual / 100);
        resultado.fechamento += filtradas.fechamento * (percentual / 100);
        resultado.categorias.push({
            codigo: produtoVirtual.codigo,
            percentual,
            base_pre_evento: filtradas.pre_evento,
            base_negociado: filtradas.negociado,
            base_fechamento: filtradas.fechamento,
        });
    });
    return resultado;
};

export const FeeIntelligence = {
    toComparableId,
    isAgencyFeeItem,
    isSanofiAgencyTierFeeItem,
    isAgencyFeeRuleItem,
    isFeeCalculatorItem,
    isVoucherManualBaseItem,
    calculateRuleBaseTotals,
    calculateRuleContributionValues,
    calculateCatalogCategoryFees,
    calculateMiniMeetingFee,
    miniMeetingParaEvento,
    rotuloMiniMeeting,
    calculateAgencyFeeLineTotal,
    calculateAgencyFeeLineTotalRaw,
    // Metadados do shadow BRL. A taxa vem da regra, nunca de valor FEE / base.
    // AbbVie: com as faixas das categorias (context.faixasCliente), o FEE AGÊNCIA real usa o
    // mesmo motor por categoria × pax do evento × tabela_versao do Pré/Negociado (espelho da
    // API: OrcamentoFeeRuleDisplay::calculateCatalogCategoryFees).
    // FEE real = mesmo total de linha do Pré/Negociado: o "% Imposto NF" da própria linha de
    // FEE (gross-up ÷ (1 − a)) continua valendo (espelho de OrcamentoFechamentoExterior::totalLinhaFee).
    calculateFeeRealMetadata(orcamentoitem = [], feeSubitem = {}, context = {}) {
        const produto = feeSubitem.produto ?? {};
        const comTotalLinha = (meta, especial) => ({
            ...meta,
            contribuicao_brl: meta.total_brl,
            imposto_nf_percentual: percentualImpostoNfLinhaFee(feeSubitem),
            total_brl: totalLinhaFee(feeSubitem, meta.total_brl, especial),
        });
        if (isAgencyFeeItem(produto)) {
            // Mini Meeting (28/09): context.miniMeeting já vem filtrado pelo tipo do evento.
            const categorias = calculateCatalogCategoryFees(orcamentoitem, context.faixasCliente || [], context.tabelaVersao ?? null, context.paxEvento ?? 0, context.miniMeeting ?? null);
            if (categorias.aplicavel) {
                const base = categorias.categorias.reduce((soma, categoria) => soma + toNumber(categoria.base_fechamento), 0);
                // % efetivo (média ponderada das categorias), só informativo.
                return comTotalLinha({ base_brl: base, percentual: base > 0 ? (categorias.fechamento / base) * 100 : null, total_brl: categorias.fechamento, completo: true,
                    ...(categorias.regra === MINI_MEETING ? { regra: rotuloMiniMeeting(categorias, 'fechamento') } : {}) }, false);
            }
            const regras = normalizeClientFeeRules(context.feeRules || []);
            const faixaBase = calculateRangeBaseTotals(orcamentoitem, feeSubitem).fechamento;
            const percentual = resolveClientFeePercent(regras, 'fechamento', faixaBase);
            const base = orcamentoitem.reduce((total, grupo) => total + (grupo.subitem || []).reduce((soma, linha) => shouldIncludeForAgencyFee(linha, feeSubitem, grupo) && !isAgencyFeeRuleItem(linha.produto) && !isVoucherManualBaseItem(linha.produto) ? soma + rawBaseByMoment(linha, 'fechamento') : soma, 0), 0);
            return comTotalLinha({ base_brl: base, percentual: percentual || null, total_brl: this.calculateAgencyFeeValues(orcamentoitem, feeSubitem, context).fechamento, completo: base <= 0 || percentual > 0 }, false);
        }
        if (isAgencyFeeRuleItem(produto)) {
            const bases = calculateRuleBaseTotals(orcamentoitem, feeSubitem);
            const valores = calculateRuleContributionValues(orcamentoitem, feeSubitem, context.tabelaVersao, context.feeRules || []);
            const percentualCalculado = valores.percentuais?.fechamento ?? 0;
            const percentual = percentualCalculado > 0 ? percentualCalculado : null;
            return comTotalLinha({ base_brl: bases.fechamento, percentual, total_brl: valores.fechamento, completo: true }, true);
        }
        return null;
    },
    calculateAgencyFeeValues(orcamentoitem = [], feeSubitem = {}, context = {}) {
        const result = emptyMomentValues();
        const debugRows = [];
        const standardFeeBaseItems = [];
        // Épico 13 — período da tabela de faixas (Abbvie: 2025 retroativo / 2026 novo)
        const tabelaVersao = context?.tabelaVersao ?? null;

        if (!Array.isArray(orcamentoitem)) {
            return result;
        }

        orcamentoitem.forEach((item) => {
            (item?.subitem || []).forEach((subitem) => {
                if (!shouldIncludeForAgencyFee(subitem, feeSubitem, item)) {
                    return;
                }

                let contribution = emptyMomentValues();
                if (isAgencyFeeRuleItem(subitem.produto)) {
                    contribution = calculateRuleContributionValues(orcamentoitem, subitem, tabelaVersao);
                    result.pre_evento += contribution.pre_evento;
                    result.negociado += contribution.negociado;
                    result.fechamento += contribution.fechamento;
                } else {
                    if (isVoucherManualBaseItem(subitem.produto)) {
                        contribution.pre_evento = itemFeeContribution(subitem, 'pre_evento');
                        contribution.negociado = itemFeeContribution(subitem, 'negociado');
                        contribution.fechamento = itemFeeContribution(subitem, 'fechamento');
                        result.pre_evento += contribution.pre_evento;
                        result.negociado += contribution.negociado;
                        result.fechamento += contribution.fechamento;
                    } else {
                        standardFeeBaseItems.push(subitem);
                    }
                }
            });
        });

        const regrasCliente = normalizeClientFeeRules(context?.feeRules ?? []);

        const totalBase = {
            pre_evento: 0,
            negociado: 0,
            fechamento: 0,
        };

        standardFeeBaseItems.forEach((subitem) => {
            totalBase.pre_evento += rawBaseByMoment(subitem, 'pre_evento');
            totalBase.negociado += rawBaseByMoment(subitem, 'negociado');
            totalBase.fechamento += rawBaseByMoment(subitem, 'fechamento');
        });

        const rangeBaseTotals = calculateRangeBaseTotals(orcamentoitem, feeSubitem);

        const percentualCliente = {
            pre_evento: resolveClientFeePercent(regrasCliente, 'pre_evento', rangeBaseTotals.pre_evento),
            negociado: resolveClientFeePercent(regrasCliente, 'negociado', rangeBaseTotals.negociado),
            fechamento: resolveClientFeePercent(regrasCliente, 'fechamento', rangeBaseTotals.fechamento),
        };

        standardFeeBaseItems.forEach((subitem) => {
            const contribution = {
                pre_evento: rawBaseByMoment(subitem, 'pre_evento') * (percentualCliente.pre_evento / 100),
                negociado: rawBaseByMoment(subitem, 'negociado') * (percentualCliente.negociado / 100),
                fechamento: rawBaseByMoment(subitem, 'fechamento') * (percentualCliente.fechamento / 100),
            };

            result.pre_evento += contribution.pre_evento;
            result.negociado += contribution.negociado;
            result.fechamento += contribution.fechamento;

            if (isFeeDebugEnabled()) {
                debugRows.push({
                    produtoId: toComparableId(subitem?.produto?.id),
                    produtoCodigo: subitem?.produto?.codigo ?? null,
                    produtoNome: subitem?.produto?.nome ?? '',
                    compoeFee: subitem?.compoeFee ?? 'nao',
                    fornecedorPagamento: subitem?.fornecedorpagamento?.id ?? subitem?.fornecedorpagamento ?? null,
                    totalPreEvento: toNumber(subitem?.totalPreEvento),
                    percentualClientePreEvento: percentualCliente.pre_evento,
                    contribuicaoPreEvento: contribution.pre_evento,
                });
            }
        });

        if (isFeeDebugEnabled()) {
            console.log('[FEE DEBUG] regra cliente', regrasCliente);
            console.log('[FEE DEBUG] base total dos itens que compoem fee', totalBase);
            console.log('[FEE DEBUG] base total para faixa do cliente (total geral sem calculadores)', rangeBaseTotals);
            console.log('[FEE DEBUG] percentual cliente por momento', percentualCliente);
        }

        if (isFeeDebugEnabled()) {
            console.log('[FEE DEBUG] base do FEE AGENCIA', debugRows);
            console.log('[FEE DEBUG] total FEE AGENCIA', result);
        }

        return result;
    }
};
